import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "./api";

export const SOLUTION_PAGES = [
  {
    slug: "hotel-restaurant-signage",
    pageKey: "hotel-restaurant-signage",
    sector: "Hotel / Restaurant",
    title: "Hotel Digital Signage & Restaurant Ordering Display Software",
    h1: "Hotel and restaurant signage that runs orders, menus and screens together.",
    h2: "Digital menu boards, QR ordering, kitchen-friendly billing and Android TV displays.",
    intro: "Give hotels, restaurants and cafes one simple control panel for menus, room ordering, offers and customer-facing displays.",
    points: ["Digital menu boards", "Hotel order taking", "Thermal bill printing", "QR storefront", "Android TV player"],
  },
  {
    slug: "salon-token-display",
    pageKey: "salon-token-display",
    sector: "Salon / Clinic Token",
    title: "Salon Token Display, Appointment Booking & Queue Management",
    h1: "Salon and clinic token display with mobile booking.",
    h2: "Customers book, receive token numbers and track live waiting time from mobile.",
    intro: "Run walk-ins and online bookings in one live queue with simple token announcements for salons, spas and clinics.",
    points: ["Customer login", "Live token status", "Average waiting time", "Service queue", "Android TV token display"],
  },
  {
    slug: "school-digital-signage",
    pageKey: "school-digital-signage",
    sector: "School / Classes",
    title: "School Digital Signage for Classes, Notices & Smart Displays",
    h1: "School and coaching class digital signage.",
    h2: "Show timetables, notices, admissions, achievements and emergency alerts.",
    intro: "A clean digital notice board system for schools, coaching classes, academies and training centres.",
    points: ["Notice display", "Class schedule", "Admission promotions", "Emergency alerts", "Multi-screen control"],
  },
  {
    slug: "housing-society-signage",
    pageKey: "housing-society-signage",
    sector: "Housing Society",
    title: "Housing Society Notice Display & Digital Signage",
    h1: "Housing society signage for notices, events and security updates.",
    h2: "Modern lobby displays for apartment communities and building entrances.",
    intro: "Publish society notices, maintenance alerts, event information and resident messages without printing paper notices.",
    points: ["Lobby notice board", "Maintenance alerts", "Event display", "Security messages", "Remote publishing"],
  },
  {
    slug: "hotel-restaurant-signage",
    pageKey: "hotel-restaurant-signage",
    sector: "Hotel / Restaurant",
    title: "Hotel / Restaurant QR Ordering, Menu & Token Display Software",
    h1: "Hotel and restaurant ordering with digital menu screens.",
    h2: "Manage QR ordering, waiter orders, kitchen status and thermal bills from one dashboard.",
    intro: "Turn any Android TV and QR table code into a connected hotel or restaurant ordering system.",
    points: ["QR ordering", "Digital menu", "Waiter dashboard", "Kitchen status", "Thermal billing"],
  },
  {
    slug: "led-interactive-panel-signage",
    pageKey: "led-interactive-panel-signage",
    sector: "LED Interactive Panel",
    title: "LED Interactive Panel for Classrooms & Meeting Rooms",
    h1: "LED interactive panels for classrooms and meeting rooms.",
    h2: "Touch-enabled displays for teaching and collaborative presentations.",
    intro: "Interactive touch panels built for classroom teaching, with meeting-room use as a secondary application.",
    points: ["Touch display", "Classroom teaching", "Meeting rooms", "Wall mount support", "Dealer platform"],
  },
  {
    slug: "led-video-wall-signage",
    pageKey: "led-video-wall-signage",
    sector: "LED TV / LED Active Display",
    title: "LED TV and LED Active Display Video Wall Signage Software",
    h1: "LED TV and LED active display video wall signage for showrooms and public spaces.",
    h2: "Schedule rich media across large display networks.",
    intro: "Control content for LED TVs, LED active display video walls, floor stands, wall mounted displays and Android-based signage players from the cloud.",
    points: ["LED active display video wall", "Android player", "Playlist scheduling", "Remote updates", "Dealer support"],
  },
  {
    slug: "bank-cooperative-society-signage",
    pageKey: "bank-cooperative-society-signage",
    sector: "Bank / Cooperative Society",
    title: "Bank and Cooperative Society Token Display Software",
    h1: "Bank and cooperative society token display system.",
    h2: "Queue display, counter calling and announcement-friendly signage.",
    intro: "Manage customer queues and service announcements for banks, cooperative societies and finance offices.",
    points: ["Token display", "Queue status", "Counter calling", "Public notices", "Android TV support"],
  },
];

export function defaultSeo(pageKey = "home") {
  const solution = SOLUTION_PAGES.find((item) => item.pageKey === pageKey || item.slug === pageKey);
  const title = solution?.title || "RP Signage | Digital Signage Software, Token System & LED Video Wall";
  const path = solution ? `/solutions/${solution.slug}` : "/";
  return {
    pageKey,
    sector: solution?.sector || "Digital Signage",
    slug: solution?.slug || "",
    metaTitle: title,
    metaDescription:
      "RP Signage by RP Infotech, Vasai Mumbai provides digital signage software, Android TV support, token systems, hotel ordering, LED TV, LED active display video walls and LED interactive panels.",
    metaKeywords:
      "RP Signage, RP Infotech, Vasai, Mumbai, Digital Signage, LED TV, LED Active Display, LED Video Wall, LED Interactive Panel, Android TV signage, token display",
    canonicalUrl: `https://rpsignage.com${path}`.replace(/\/$/, ""),
    ogTitle: title,
    ogDescription: "Cloud digital signage software for businesses, dealers, Android TV screens and local display networks.",
    ogImage: "https://rpsignage.com/rp-signage-hero-banner.png",
    twitterTitle: title,
    twitterDescription: "Cloud digital signage software, token display, hotel ordering and LED display solutions.",
    twitterImage: "https://rpsignage.com/rp-signage-hero-banner.png",
    h1: solution?.h1 || "Digital Signage Software for Hotels, Restaurants, Salons, Schools, Housing Societies & Businesses",
    h2: solution?.h2 || "Manage Screens, Orders, Tokens, Menus, Announcements and Customer Engagement from Anywhere.",
    robots: "index, follow",
    schemaJson: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Does RP Signage work on Android TV?",
          acceptedAnswer: { "@type": "Answer", text: "Yes. RP Signage supports Android TV and Android signage players." },
        },
        {
          "@type": "Question",
          name: "Can dealers manage clients?",
          acceptedAnswer: { "@type": "Answer", text: "Yes. Dealers can manage clients, wallets, subscriptions, templates and screens." },
        },
      ],
    }),
    localBusinessJson: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "RP Infotech",
      url: "https://rpsignage.com",
      areaServed: ["Vasai", "Virar", "Mumbai", "Maharashtra", "India"],
      makesOffer: ["Digital Signage Software", "LED TV", "LED Active Display / Video Wall", "LED Interactive Panel", "LED Floor Stand Display", "LED Wall Mounted Display"],
      address: {
        "@type": "PostalAddress",
        addressLocality: "Vasai",
        addressRegion: "Maharashtra",
        addressCountry: "IN",
      },
    }),
    isActive: true,
  };
}

function absoluteSeoUrl(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^https?:\/\//i.test(text) || text.startsWith("data:")) return text;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://rpsignage.com";
  return `${origin}${text.startsWith("/") ? text : `/${text}`}`;
}

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") el.setAttribute(key, value);
  });
}

function upsertJsonLd(id, raw) {
  let el = document.getElementById(id);
  if (!raw) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = typeof raw === "string" ? raw : JSON.stringify(raw);
}

export function applySeoTags(seoInput = {}) {
  if (typeof document === "undefined") return;
  const seo = { ...defaultSeo(seoInput.pageKey || "home"), ...seoInput };
  const title = seo.metaTitle || seo.seo_title || seo.ogTitle || "RP Signage";
  const description = seo.metaDescription || seo.meta_description || "";
  const keywords = seo.metaKeywords || seo.meta_keywords || "";
  const canonical = seo.canonicalUrl || seo.canonical_url || "";
  const image = absoluteSeoUrl(seo.ogImage || seo.og_image_url || seo.twitterImage || "");
  const pageUrl = absoluteSeoUrl(seo.ogUrl || seo.canonicalUrl || seo.canonical_url || (typeof window !== "undefined" ? window.location.pathname : "/"));
  document.title = title;
  upsertMeta('meta[name="description"]', { name: "description", content: description });
  upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
  upsertMeta('meta[name="robots"]', { name: "robots", content: seo.robots || "index, follow" });
  upsertMeta('meta[property="og:title"]', { property: "og:title", content: seo.ogTitle || title });
  upsertMeta('meta[property="og:description"]', { property: "og:description", content: seo.ogDescription || description });
  upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
  upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: image });
  upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: seo.ogImageWidth || "1200" });
  upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: seo.ogImageHeight || "630" });
  upsertMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl });
  upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seo.twitterTitle || seo.ogTitle || title });
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: seo.twitterDescription || seo.ogDescription || description });
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: seo.twitterImage || image });
  if (canonical) upsertMeta('link[rel="canonical"]', { rel: "canonical", href: canonical });
  upsertJsonLd("rp-seo-schema", seo.schemaJson);
  upsertJsonLd("rp-local-business-schema", seo.localBusinessJson);
}

export function useSeo(pageKey, fallback = {}) {
  const fallbackKey = JSON.stringify(fallback || {});
  const fallbackSeo = useMemo(() => {
    let parsed = {};
    try {
      parsed = JSON.parse(fallbackKey || "{}");
    } catch {
      parsed = {};
    }
    return { ...defaultSeo(pageKey), ...parsed };
  }, [pageKey, fallbackKey]);
  const [seo, setSeo] = useState(fallbackSeo);

  useEffect(() => {
    let mounted = true;
    setSeo(fallbackSeo);
    api
      .get(`/seo/${encodeURIComponent(pageKey || "home")}`)
      .then(({ data }) => {
        if (mounted) setSeo({ ...fallbackSeo, ...(data || {}) });
      })
      .catch(() => {
        if (mounted) setSeo(fallbackSeo);
      });
    return () => {
      mounted = false;
    };
  }, [pageKey, fallbackSeo]);

  useEffect(() => {
    applySeoTags(seo);
  }, [seo]);

  return seo;
}

export function absoluteApiMedia(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE.replace(/\/api$/, "")}${String(path).startsWith("/") ? "" : "/"}${path}`;
}
